<template>
  <div style="margin:20px">
    <div style="width: 100%; background-color: #eee; border: 1px dotted grey; margin-bottom: 25px;">
      <span style="font-size: 26px; font-weight: bold">Driver Comparison</span><br/>
      <span style="font-style: italic; font-size: 18px">Compare multiple drivers side-by-side and view company averages</span>
    </div>

    <div style="position: relative; font-weight: bold; font-size: 24px" class="align-right d-flex align-items-center justify-content-center">
      <a href="#" class="white-link" v-on:click.prevent="shift_date_range(-1)"><</a>
      &nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="start_date" :config="options" :placeholder="start_date"/>
      &nbsp;to&nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="end_date" :config="options" :placeholder="end_date"/>
      &nbsp;<a href="#" class="white-link" v-on:click.prevent="shift_date_range(1)">></a>
      &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;
      <b-button v-on:click.prevent="load_comparison()" :disabled="loading">Load Comparison</b-button>
      &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;
      <b-button v-on:click.prevent="select_all_drivers()" variant="secondary">Select All Drivers</b-button>
    </div>

    <div v-if="loading" style="text-align: center; margin: 50px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <div v-else-if="comparison_data && comparison_data.length > 0" style="margin: 20px;">
      <b-card>
        <h3>Driver Comparison</h3>
        <p>Date Range: {{ formatDate(start_date) }} to {{ formatDate(end_date) }}</p>
        <p v-if="company_averages">Total Drivers: {{ company_averages.total_drivers || comparison_data.length }}</p>
        
        <hr/>

        <!-- Company Averages -->
        <div v-if="company_averages" style="margin-bottom: 30px;">
          <h4>Company Averages</h4>
          <b-row>
            <b-col md="3">
              <b-card bg-variant="info" text-variant="white">
                <h5>Avg Risk Score</h5>
                <h3>{{ formatNumber(company_averages.average_risk_score, 2) }}</h3>
              </b-card>
            </b-col>
            <b-col md="3">
              <b-card bg-variant="info" text-variant="white">
                <h5>Avg Risk Rank</h5>
                <h3>{{ formatNumber(company_averages.average_risk_rank, 2) }}</h3>
              </b-card>
            </b-col>
            <b-col md="3">
              <b-card bg-variant="info" text-variant="white">
                <h5>Avg Idling %</h5>
                <h3>{{ formatNumber(company_averages.average_idling_percentage, 2) }}%</h3>
              </b-card>
            </b-col>
            <b-col md="3">
              <b-card bg-variant="info" text-variant="white">
                <h5>Avg MPG</h5>
                <h3>{{ formatNumber(company_averages.average_miles_per_gallon, 2) }}</h3>
              </b-card>
            </b-col>
            <b-col md="3">
              <b-card bg-variant="info" text-variant="white">
                <h5>Avg Revenue/Mile</h5>
                <h3>${{ formatNumber(company_averages.average_revenue_per_mile, 2) }}</h3>
              </b-card>
            </b-col>
          </b-row>
        </div>

        <hr/>

        <!-- Driver Comparison Table -->
        <b-table 
          :items="comparison_data" 
          :fields="table_fields" 
          bordered 
          striped 
          hover
          sticky-header="70vh"
          style="font-size: 16px">
          
          <template slot="driver_name" slot-scope="row">
            <strong>{{ row.item.driver_name || 'N/A' }}</strong>
          </template>

          <template slot="risk_score" slot-scope="row">
            {{ row.item.risk_score || 'N/A' }}
            <span v-if="company_averages" :class="getComparisonClass(row.item.risk_score, company_averages.average_risk_score)">
              ({{ getComparisonIndicator(row.item.risk_score, company_averages.average_risk_score) }})
            </span>
          </template>

          <template slot="risk_score_rank" slot-scope="row">
            {{ row.item.risk_score_rank || 'N/A' }}
          </template>

          <template slot="idling_percentage" slot-scope="row">
            {{ formatNumber(row.item.idling_percentage, 2) }}%
            <span v-if="company_averages" :class="getComparisonClass(row.item.idling_percentage, company_averages.average_idling_percentage, true)">
              ({{ getComparisonIndicator(row.item.idling_percentage, company_averages.average_idling_percentage, true) }})
            </span>
          </template>

          <template slot="speeding_percentage" slot-scope="row">
            {{ formatNumber(row.item.speeding_percentage, 2) || 0 }}%
            <span v-if="company_averages" :class="getComparisonClass(row.item.speeding_percentage, company_averages.average_speeding_percentage, true)">
              ({{ getComparisonIndicator(row.item.speeding_percentage, company_averages.average_speeding_percentage, true) }})
            </span>
          </template>

          <template slot="miles_per_gallon" slot-scope="row">
            {{ formatNumber(row.item.miles_per_gallon, 2) || 'N/A' }}
            <span v-if="company_averages" :class="getComparisonClass(row.item.miles_per_gallon, company_averages.average_miles_per_gallon)">
              ({{ getComparisonIndicator(row.item.miles_per_gallon, company_averages.average_miles_per_gallon) }})
            </span>
          </template>

          <template slot="fuel_consumed_gallons" slot-scope="row">
            {{ formatNumber(row.item.fuel_consumed_gallons, 2) || '0.00' }}
          </template>

          <template slot="miles_driven" slot-scope="row">
            {{ formatNumber(row.item.miles_driven || row.item.distance_traveled_miles, 2) }}
          </template>

          <template slot="dispatch_count" slot-scope="row">
            {{ row.item.dispatch_count || 0 }}
          </template>

          <template slot="absence_count" slot-scope="row">
            {{ row.item.absence_count || 0 }}
          </template>
        </b-table>
      </b-card>
    </div>

    <div v-else-if="!loading" style="text-align: center; margin: 50px; color: #666;">
      <p>No comparison data available. Click "Load Comparison" to fetch data.</p>
    </div>
  </div>
</template>

<script>
import {mapState} from "vuex"
import {justDate} from '../helpers/dates'
import moment from 'moment'
import {get_driver_comparison} from '../networking/tbn_apis'

export default {
  name: 'driver_comparison',

  components: {
  },

  computed: {
    ...mapState({
      user: state => state.profile.user
    })
  },

  data() {
    return {
      loading: false,
      comparison_data: [],
      company_averages: null,
      start_date: justDate(moment('2025-01-01', 'YYYY-MM-DD')),
      end_date: justDate(moment('2025-12-31', 'YYYY-MM-DD')),
      table_fields: [
        { key: 'driver_name', label: 'Driver Name', sortable: true },
        { key: 'risk_score', label: 'Risk Score', sortable: true },
        { key: 'risk_score_rank', label: 'Risk Rank', sortable: true },
        { key: 'idling_percentage', label: 'Idling %', sortable: true },
        { key: 'speeding_percentage', label: 'Speeding %', sortable: true },
        { key: 'miles_per_gallon', label: 'Miles per Gallon', sortable: true },
        { key: 'fuel_consumed_gallons', label: 'Gallons Burned', sortable: true },
        { key: 'miles_driven', label: 'Miles Driven', sortable: true },
        { key: 'customer_requests', label: 'Customer Requests', sortable: true },
        { key: 'total_revenue', label: 'Total Revenue', sortable: true, visible: false },
        { key: 'revenue_per_mile', label: 'Revenue/Mile', sortable: true, visible: false },
        { key: 'absence_count', label: 'Absences', sortable: true }
      ],
      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(365, 'days'),
        maxDate: moment(),
        keepOpen: false,
        showClear: true,
        showClose: true,
      },
    }
  },

  methods: {
    async load_comparison() {
      this.loading = true
      this.comparison_data = []
      this.company_averages = null

      try {
        const start_month = moment(this.start_date).format('YYYY-MM')
        const end_month = moment(this.end_date).format('YYYY-MM')
        
        const response = await get_driver_comparison(
          this.user.tenant,
          null,
          start_month,
          end_month
        )

        if (response && response.data) {
          this.comparison_data = response.data.results || []
          this.company_averages = (response.data.summary && response.data.summary.company_averages) || null
        }
      } catch (error) {
        console.error('Error loading comparison:', error)
        this.$bvToast.toast('Error loading comparison data', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.loading = false
      }
    },

    select_all_drivers() {
      // This would be used if we had multi-select driver dropdown
      // For now, load_comparison loads all drivers by default
      this.load_comparison()
    },

    shift_date_range(days) {
      const start = moment(this.start_date)
      const end = moment(this.end_date)
      
      start.add(days, 'days')
      end.add(days, 'days')
      
      this.start_date = justDate(start)
      this.end_date = justDate(end)
    },

    formatDate(date) {
      return moment(date).format('MM/DD/YYYY')
    },

    formatNumber(value, decimals = 2) {
      if (value === null || value === undefined) {
        return '0.00'
      }
      return Number(value).toFixed(decimals)
    },

    getComparisonClass(value, average, lowerIsBetter = false) {
      if (!value || !average) return ''
      const diff = value - average
      if (lowerIsBetter) {
        return diff < 0 ? 'text-success' : diff > 0 ? 'text-danger' : ''
      } else {
        return diff > 0 ? 'text-success' : diff < 0 ? 'text-danger' : ''
      }
    },

    getComparisonIndicator(value, average, lowerIsBetter = false) {
      if (!value || !average) return ''
      const diff = value - average
      const percentDiff = ((diff / average) * 100).toFixed(1)
      if (lowerIsBetter) {
        return diff < 0 ? `↓${Math.abs(percentDiff)}%` : diff > 0 ? `↑${percentDiff}%` : '='
      } else {
        return diff > 0 ? `↑${percentDiff}%` : diff < 0 ? `↓${Math.abs(percentDiff)}%` : '='
      }
    }
  }
}
</script>

<style scoped>
.white-link {
  color: white;
  text-decoration: none;
  font-size: 24px;
}
.white-link:hover {
  color: #ccc;
}
.text-success {
  color: #28a745;
  font-weight: bold;
}
.text-danger {
  color: #dc3545;
  font-weight: bold;
}
</style>

